/**
 * WorkshopsSchedule — "THE SCHEDULE".
 *
 * A break-out-width, super-organized brutalist *schedule board* of the next
 * three upcoming workshop dates — one row per series, soonest first. NOT a card
 * grid, NOT a poster wall, NOT the cinema marquee. The structure reads like a
 * departure board / printed agenda: a heavy charcoal frame, a mono UPPERCASE
 * column-label header row, then a FULLY BORDERED grid (every cell boxed —
 * left/right edges, vertical separators between every column, and horizontal
 * rules between rows):
 *
 *   [ DATE | WORKSHOP (image + icon-above-name + descriptor) | FORMAT | TIME | RESERVE ]
 *
 * EXACTLY FIVE COLUMNS. The DATE block anchors the far left (big blocky stacked
 * numerals, centered). The WORKSHOP cell is one combined field: the hero IMAGE
 * fills the left edge-to-edge (object-cover, full row height — no letterbox),
 * and to its right the (large) riso ICON sits ABOVE the serif title, then a
 * one-line descriptor. FORMAT shows a single greyed Zoom glyph (every session
 * runs live on Zoom). TIME shows a start–end range in ET.
 *
 * ── ROW-HOVER SIGNATURE ───────────────────────────────────────────────────
 * Hovering anywhere in a row sweeps a deep-purple (--indigo-deep) overlay in
 * left→right, inverts all row text to cream, lights the reserve arrow terra
 * over faint-cream matrix dots, and lifts the Zoom glyph to a readable light
 * treatment. Implemented via `.schedule-row-fill::before` (scaleX 0→1) +
 * `.schedule-ink`/`.schedule-ink-muted` color swaps in index.css. Honors
 * prefers-reduced-motion (no sweep; instant fill + color).
 *
 * ── GRID MOTIF (native to the site) ───────────────────────────────────────
 * Horizontal row rules ONLY — the list reads top-to-bottom, no column rules,
 * no frame border, and NO corner/registration marks (CornerMarks/CrosshairMark
 * are banned from the design — see client/CLAUDE.md). The dot-matrix arrow in
 * the Reserve column is the one loud dotted moment.
 *
 * ── RESERVE = DOT-MATRIX ARROW (the signature) ────────────────────────────
 * The Reserve affordance is an ARROW rendered as a field of <circle> dots that
 * form a right-pointing → . At REST the dots sit in a muted grey (--charcoal-30,
 * "greyed out"). On row HOVER the dots ignite to terra, staggered tail → head
 * (per-dot transition-delay keyed to --i) so the arrow "fires" toward the
 * action. The PRICE ("$599") rides alongside the arrow, so the row still reads
 * "$599 →". The whole row is a link to /workshops/<seriesSlug>. Honors
 * prefers-reduced-motion (instant color, no stagger).
 *
 * Imagery: series WITH a real hero photo get a duotone treatment (grayscale +
 * charcoal multiply + cream screen — on-brand monotone) filling the image area.
 * Those WITHOUT a photo (AI-First) get their soft --poster-<key> color field
 * with a quiet dot ground, so every row stays consistent and organized.
 *
 * Data: live via useUpcomingWorkshops() — soonest upcoming published session per
 * series, lowest "from" price, series names, riso icon keys, split date parts,
 * start time + duration (ET), next-up slug. Sliced to the next three.
 *
 * Design-system compliance: all color/type/spacing/border via tokens + utility
 * classes; no box-shadow, no gradients on backgrounds/buttons (the duotone uses
 * mix-blend on a photo, not a background gradient); border-radius 0 on every
 * structural cell. Honors prefers-reduced-motion.
 */
import { Link } from "wouter";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import {
  useUpcomingWorkshops,
  ICON_BASE,
  type PosterModel,
} from "@/hooks/use-upcoming-workshops";

const mechanicalEase = [0.25, 0, 0.25, 1] as const;

/** Zoom brand logo — small format glyph next to the "LIVE" label. */
const ZOOM_LOGO = "/images/cowork-catalog/connectors/zoom.svg";

/* ── Per-series presentation (descriptor, level tag, hero photo) ──────────────
   The hook supplies the live date / time / price / name / icon. The static
   board copy lives here, keyed by the canonical series slug (dates are
   variables; slugs are stable). `photo: null` => render the tinted color-field
   in the image area instead of a duotone thumbnail. `focal` crops the photo to
   the workshop's best frame. Descriptors are clamped to ONE line in the UI, so
   keep them tight. */
type Presentation = {
  descriptor: string;
  level: string;
  /* `riso: true` => a full-color risograph hero (same press run for all six);
     it renders edge-to-edge with NO duotone/grayscale treatment so the flat
     spot-color ink survives. `riso` omitted => legacy photo gets the duotone. */
  photo: { jpg: string; webp?: string; avif?: string; focal: string; riso?: boolean } | null;
};

/* Shared risograph press run (Nano Banana Pro). One unified screenprint per
   series — burnt orange / teal-navy / gold on cream, heavy halftone grain.
   Cropped into the narrow image slot via object-cover; `focal` keeps the human
   subject in frame. */
const RISO_BASE = "/images/workshop/schedule";

const PRESENTATION: Record<string, Presentation> = {
  "claude-for-design-workshop": {
    descriptor: "Blank page to a built, designed landing page by lunch.",
    level: "Beginner",
    photo: { jpg: `${RISO_BASE}/riso-claude-design.png`, focal: "42% 45%", riso: true },
  },
  "claude-code-for-beginners-workshop": {
    descriptor: "Install Claude Code, build something real, ship it live.",
    level: "Beginner",
    photo: { jpg: `${RISO_BASE}/riso-claude-code.png`, focal: "45% 45%", riso: true },
  },
  "the-ai-first-leader-workshop": {
    descriptor: "Frameworks to rebuild your company around AI, plus a 90-day plan.",
    level: "Leadership",
    photo: { jpg: `${RISO_BASE}/riso-ai-leader.png`, focal: "66% 45%", riso: true },
  },
  "advanced-claude-code-workshop": {
    descriptor: "Make Claude Code survive production: security, correctness, ops.",
    level: "Advanced",
    photo: { jpg: `${RISO_BASE}/riso-claude-code-advanced.png`, focal: "55% 48%", riso: true },
  },
  "claude-cowork-for-beginners-workshop": {
    descriptor: "Launch autonomous agents and leave with automations running.",
    level: "Beginner",
    // Claymation hero (Isaac + clay robot) — full-color, no duotone (riso path = full color).
    photo: { jpg: "/images/claude-cowork-hero-clay-shoulder.webp", focal: "64% 40%", riso: true },
  },
  "claude-for-sales-teams-workshop": {
    descriptor: "Run a full B2B pipeline with Claude as the operator.",
    level: "Intermediate",
    photo: { jpg: `${RISO_BASE}/riso-claude-sales.png`, focal: "60% 45%", riso: true },
  },
};

/* Structural border for the fully-bordered grid — 1.5px charcoal, continuous. */
const CELL_BORDER = "1.5px solid var(--charcoal)";

/* Five-column track: DATE | WORKSHOP | FORMAT | TIME | RESERVE. Applied via
   inline `gridTemplateColumns` (NOT an arbitrary Tailwind class) so the value is
   never built through string interpolation — Tailwind v4's JIT can't see
   interpolated class names. Shared by the header row + every data row so the
   columns line up continuously. `lg:grid` toggles it on at the lg breakpoint;
   the inline template only takes effect once the element is `display:grid`.
   FORMAT is narrow (just the greyed Zoom glyph); TIME is wider now that it shows
   a full start–end range. No vertical rules anywhere — horizontal row rules
   only. */
const GRID_COLS =
  "clamp(124px,11vw,152px) minmax(0,1fr) clamp(78px,8vw,110px) clamp(150px,13vw,196px) clamp(168px,15vw,212px)";

/* ── Dot-matrix arrow ─────────────────────────────────────────────────────────
   The Reserve affordance, per the brief: "all dot matrix with an arrow in a
   darker shade, sort of greyed out, but on hover the arrow dots transform into a
   highlight color." So it is a true DOT MATRIX — a regular lattice of faint dots
   — with a right-pointing → picked out in the darker/igniting dots:
     • LATTICE: every grid point, very faint (--charcoal-14), static. The matrix.
     • ARROW: the subset forming → (shaft + chevron head). Muted grey
       (--charcoal-30) at rest; on row hover each ignites to terra, staggered
       tail → head via per-dot transition-delay keyed to `--i` (CSS in index.css)
       so the arrow "fires" toward the action. The tip dot gets `dot-arrow-tip`
       for a tiny forward nudge — the "fire" landing.

   Geometry: a 5-row × 10-col lattice on a 71×26 viewBox (cols every 7px from
   x=4, rows every 5px from y=3 — true center row at y=13). The arrow is the
   center-row shaft + a clean chevron converging on the tip. Arrow dots are
   ordered by x so `--i` (and the ignition wave) increases tail → tip. */
/* The dot matrix fills the entire reserve cell. A regular COLS×ROWS lattice of
   equal-size dots; the arrow is picked out ONLY by shade (a thick shaft + solid
   triangular head). Cell aspect ≈ 1.4:1, so the lattice is sized to match and
   fills the box via preserveAspectRatio. */
const COLS = 13;
const ROWS = 9;
const GAP = 9;
const PAD = 8;
const DOT_R = 2.2;
const VBW = PAD * 2 + (COLS - 1) * GAP; // 124
const VBH = PAD * 2 + (ROWS - 1) * GAP; // 88
const CENTER = 4; // center row index

/** Is lattice point (col,row) part of the thick right-pointing arrow? */
function isArrowDot(c: number, r: number): boolean {
  // shaft — cols 1..7, 3 dots thick
  if (c >= 1 && c <= 7 && r >= CENTER - 1 && r <= CENTER + 1) return true;
  // solid triangular head — base at col 8, converging to the tip at col 11
  if (c >= 8 && c <= 11) {
    const spread = 11 - c; // 8→3, 9→2, 10→1, 11→0
    if (r >= CENTER - spread && r <= CENTER + spread) return true;
  }
  return false;
}

type GridDot = { cx: number; cy: number; arrow: boolean };
const GRID_DOTS: GridDot[] = (() => {
  const out: GridDot[] = [];
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      out.push({ cx: PAD + c * GAP, cy: PAD + r * GAP, arrow: isArrowDot(c, r) });
    }
  }
  return out;
})();

function DotMatrixArrow() {
  // One uniform grid of equal-size dots filling the cell. The arrow is shown
  // ONLY by shade: matrix dots faint (--charcoal-14), arrow dots darker grey
  // (--charcoal-50) at rest, igniting to terra together on a simple eased
  // hover of the reserve box. Every dot is the same radius.
  return (
    <svg
      viewBox={`0 0 ${VBW} ${VBH}`}
      fill="none"
      aria-hidden="true"
      className="dot-arrow-svg block w-full h-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {GRID_DOTS.map((d) => (
        <circle
          key={`${d.cx}-${d.cy}`}
          cx={d.cx}
          cy={d.cy}
          r={DOT_R}
          className={d.arrow ? "dot-arrow-dot" : "dot-arrow-bg"}
        />
      ))}
    </svg>
  );
}

/* ── Riso icon ───────────────────────────────────────────────────────────── */
function RisoIcon({ iconKey, size }: { iconKey: string; size: number }) {
  const base = `${ICON_BASE}/${iconKey}`;
  return (
    <picture>
      <source srcSet={`${base}.webp`} type="image/webp" />
      <img
        src={`${base}.png`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        aria-hidden="true"
        style={{ width: size, height: size, objectFit: "contain", display: "block" }}
      />
    </picture>
  );
}

/* ── Image area — fills its slot edge-to-edge (no letterbox) ──────────────────
   Photo series: a duotone-monotone cover image (grayscale → charcoal multiply →
   cream screen). Photo-less series: their soft --poster-<key> color field with a
   quiet dot ground (the icon lives above the title, not here). object-cover +
   full height means the image always fills the cell. */
function ImageArea({ row, alt }: { row: PosterModel; alt: string }) {
  const pres = PRESENTATION[row.seriesSlug];
  if (pres?.photo) {
    const photo = pres.photo;
    // Riso heroes: full-color, edge-to-edge, NO duotone — the flat spot-color
    // ink IS the treatment. (Legacy non-riso photos keep the duotone path below.)
    if (photo.riso) {
      return (
        <div className="absolute inset-0 overflow-hidden bg-cream">
          <img
            src={photo.jpg}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: photo.focal }}
            loading="lazy"
            decoding="async"
          />
        </div>
      );
    }
    return (
      <div className="absolute inset-0 overflow-hidden bg-charcoal">
        <picture>
          {photo.avif && <source srcSet={photo.avif} type="image/avif" />}
          {photo.webp && <source srcSet={photo.webp} type="image/webp" />}
          <img
            src={photo.jpg}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: photo.focal, filter: "grayscale(1) contrast(1.16) brightness(0.95)" }}
            loading="lazy"
            decoding="async"
          />
        </picture>
        {/* charcoal multiply — drives shadows to brand black */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "var(--charcoal)", mixBlendMode: "multiply", opacity: 0.5 }}
          aria-hidden="true"
        />
        {/* cream screen — lifts highlights to cream (warm monotone) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "var(--cream)", mixBlendMode: "screen", opacity: 0.26 }}
          aria-hidden="true"
        />
      </div>
    );
  }
  // Photo-less: tinted color-field with a quiet dot ground (matches the section
  // dot language — small, structural, never loud).
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ background: row.tone.field }}
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-dot-matrix-sm pointer-events-none" />
    </div>
  );
}

/* ── Client logo row ─────────────────────────────────────────────────────────
   Greyed-out proof strip above the board. NOT a marquee/slider — a static row
   that fades its logos in one-by-one on scroll (staggered). Each logo is muted
   via CSS `filter: grayscale(1) opacity(...)` so the framer fade can drive the
   `opacity` property 0→1 independently; hover lifts a single logo to full ink. */
/* `scale` nudges an individual logo's height relative to the shared base so the
   row reads optically even — wordmarks vs marks carry different visual weight. */
const LOGO_BASE_H = "clamp(20px,2.1vw,30px)";
const CLIENT_LOGOS: { src: string; alt: string; scale?: number }[] = [
  { src: "/images/client-logos/tmobile.webp", alt: "T-Mobile" },
  { src: "/images/client-logos/optimizely.svg", alt: "Optimizely" },
  { src: "/images/client-logos/mixpanel.png", alt: "Mixpanel" },
  { src: "/images/client-logos/pbs.svg", alt: "PBS" },
  { src: "/images/client-logos/asurion.webp", alt: "Asurion", scale: 1.45 },
  { src: "/images/client-logos/paypal.webp", alt: "PayPal" },
  { src: "/images/client-logos/citibank.svg", alt: "Citibank" },
];

function ClientLogoRow({ reduce }: { reduce: boolean }) {
  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.08 } },
  };
  const item: Variants = reduce
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: mechanicalEase } },
      };
  return (
    <motion.ul
      className="flex flex-wrap items-center justify-center md:justify-between gap-x-[clamp(24px,3vw,40px)] gap-y-7 list-none p-0 m-0"
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.6 }}
      aria-label="Teams we have trained"
    >
      {CLIENT_LOGOS.map((logo) => (
        <motion.li key={logo.alt} variants={item} className="shrink-0 flex items-center">
          <img
            src={logo.src}
            alt={logo.alt}
            className="schedule-logo block w-auto object-contain"
            style={{ height: logo.scale ? `calc(${LOGO_BASE_H} * ${logo.scale})` : LOGO_BASE_H }}
            loading="lazy"
            decoding="async"
          />
        </motion.li>
      ))}
    </motion.ul>
  );
}

/* ── A single schedule row ─────────────────────────────────────────────────── */
function ScheduleRow({
  row,
  isNextUp,
  variant,
}: {
  row: PosterModel;
  isNextUp: boolean;
  variant: Variants;
}) {
  const pres = PRESENTATION[row.seriesSlug];
  const hasDate = row.month !== null;

  return (
    <motion.div
      variants={variant}
      // Each row triggers its OWN entrance on scroll — independent of any parent
      // stagger orchestration. The list's rows can mount asynchronously as live
      // data resolves; a parent-orchestrated `once` animation would leave a
      // late-mounting row stuck at opacity:0 (the missing-third-row bug). Per-row
      // whileInView is resilient to that.
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      role="listitem"
      style={{ borderBottom: CELL_BORDER }}
    >
      <Link
        href={`/workshops/${row.seriesSlug}`}
        className="schedule-row group relative block no-underline focus:outline-none"
      >
        {/* Row-hover signature: a deep-purple overlay sweeps left→right behind
            the row content (`.schedule-row-fill::before`), text inverts to
            cream, the arrow lights terra. Hover fires on the WHOLE row. The
            overlay layer sits at z-0 (pointer-events:none in CSS); content is
            z-10 above it. */}
        <div
          className="schedule-row-fill relative flex flex-col lg:grid lg:items-stretch isolate"
          style={{
            gridTemplateColumns: GRID_COLS,
            // NOTE: the row divider lives on the OUTER listitem wrapper, not here —
            // so the hover ::before fill (inset:0) covers this box edge-to-edge
            // with no sub-pixel cream gap above the border (fixes the white line).
          }}
        >
          {/* ── 1 · DATE — big stacked numerals, centered in the cell (the anchor) ── */}
          <div
            className="relative z-10 flex flex-col items-center justify-center text-center py-[clamp(14px,1.8vw,24px)] px-2 border-b lg:border-b-0"
            style={{ borderBottomColor: "var(--charcoal-30)" }}
          >
            {hasDate ? (
              <>
                <span
                  className="schedule-ink leading-none text-charcoal uppercase"
                  style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "clamp(13px,1.1vw,15px)", letterSpacing: "var(--tracking-label)" }}
                >
                  {row.month}
                </span>
                <span
                  className="schedule-ink leading-none text-charcoal"
                  style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "clamp(36px,3.6vw,54px)", fontVariantNumeric: "tabular-nums", marginTop: 2 }}
                >
                  {row.day}
                </span>
                <span
                  className="schedule-ink-muted leading-none text-charcoal-muted uppercase mt-1"
                  style={{ fontFamily: "var(--font-mono)", fontWeight: 500, fontSize: "clamp(9px,0.8vw,11px)", letterSpacing: "1.2px" }}
                >
                  {row.weekday}
                </span>
              </>
            ) : (
              <span
                className="schedule-ink-muted leading-tight text-charcoal-muted uppercase text-center"
                style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "clamp(11px,1vw,13px)", letterSpacing: "1.2px" }}
              >
                New
                <br />
                date
                <br />
                soon
              </span>
            )}
          </div>

          {/* ── 2 · WORKSHOP — image (fills left) + larger icon-above-title + descriptor ── */}
          <div
            className="relative z-10 min-w-0 flex flex-col sm:flex-row items-stretch overflow-hidden border-b lg:border-b-0"
            style={{ borderBottomColor: "var(--charcoal-30)" }}
          >
            {/* image fills its fixed-width slot edge-to-edge, full row height */}
            <div
              className="relative w-full sm:w-[clamp(184px,17vw,228px)] shrink-0 self-stretch min-h-[120px] sm:min-h-0"
            >
              <ImageArea row={row} alt="" />
            </div>

            {/* text block to the right of the image */}
            <div className="relative z-10 min-w-0 flex-1 flex flex-col justify-center gap-2 py-[clamp(24px,2.8vw,40px)] px-[clamp(16px,1.8vw,26px)]">
              <RisoIcon iconKey={row.iconKey} size={62} />
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="schedule-ink schedule-name type-h4 lg:type-h3 text-charcoal leading-tight !mb-0">
                  {row.title}
                </h3>
              </div>
              <p
                className="schedule-ink-muted type-body-sm text-charcoal-muted !mb-0 max-w-[46ch] overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {pres?.descriptor}
              </p>
            </div>
          </div>

          {/* ── 3 · FORMAT — greyed Zoom logo only (every workshop runs live on
                Zoom). Label/value row on mobile, centered glyph on desktop. ── */}
          <div
            className="relative z-10 flex flex-row lg:flex-col justify-between lg:justify-center items-center gap-1 py-[clamp(12px,1.4vw,20px)] px-[clamp(16px,2vw,24px)] lg:px-2 border-b lg:border-b-0"
            style={{ borderBottomColor: "var(--charcoal-30)" }}
          >
            <span className="schedule-ink-muted type-nav uppercase text-charcoal-muted leading-none lg:hidden" style={{ marginBottom: 0 }}>
              Format
            </span>
            <img
              src={ZOOM_LOGO}
              alt="Live on Zoom"
              width={58}
              height={58}
              loading="lazy"
              decoding="async"
              className="schedule-zoom-logo"
              style={{ width: "clamp(44px,4.4vw,62px)", height: "auto", display: "block" }}
            />
          </div>

          {/* ── 4 · TIME — start–end range (ET). Label/value row on mobile, stack on desktop ── */}
          <div
            className="relative z-10 flex flex-row lg:flex-col justify-between lg:justify-center items-center lg:items-center text-left lg:text-center gap-1 py-[clamp(12px,1.4vw,20px)] px-[clamp(16px,2vw,24px)] lg:px-2 border-b lg:border-b-0"
            style={{ borderBottomColor: "var(--charcoal-30)" }}
          >
            <span className="schedule-ink-muted type-nav uppercase text-charcoal-muted leading-none lg:hidden" style={{ marginBottom: 0 }}>
              Time
            </span>
            {row.timeRange ? (
              <span
                className="schedule-ink flex flex-col text-charcoal text-right lg:text-center"
                style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "clamp(12px,1vw,14px)", letterSpacing: "0.3px", lineHeight: 1.35 }}
              >
                <span>{row.timeRange.split(" – ")[0]}</span>
                <span>– {row.timeRange.split(" – ")[1]}</span>
              </span>
            ) : (
              <span
                className="schedule-ink-muted leading-none text-charcoal-muted"
                style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "clamp(13px,1.1vw,15px)" }}
              >
                TBA
              </span>
            )}
          </div>

          {/* ── 5 · RESERVE — the cell IS a single uniform dot grid that fills
                the whole box; the arrow is shaded into it (dark charcoal at
                rest). On ROW hover the arrow dots ignite terra and the matrix
                dots lift to a faint cream so they read on the dark-purple fill.
                No price, no label — the column header reads RESERVE; the arrow
                IS the CTA. */}
          <div className="relative z-10 flex items-stretch justify-stretch self-stretch min-h-[120px] lg:min-h-0 p-[clamp(10px,1vw,16px)]">
            <DotMatrixArrow />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* ── The board ─────────────────────────────────────────────────────────────── */
export function WorkshopsSchedule() {
  const reduce = useReducedMotion() ?? false;
  const { posters, nextUpSlug } = useUpcomingWorkshops();

  // Only the next THREE upcoming workshops, soonest-first (the hook already
  // sorts by soonest date; series with no upcoming session sink to the end).
  const rows = posters.slice(0, 3);

  const headlineContainer: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: 0.05 } },
  };
  const headlineLine: Variants = reduce
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 26 },
        show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: mechanicalEase } },
      };
  const rowVariant: Variants = reduce
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 18 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: mechanicalEase } },
      };

  return (
    <section className="relative bg-cream section-padding-y overflow-hidden" data-testid="section-schedule">
      {/* ── Headline + greyed logo row at STANDARD container width ── */}
      <div className="container-content">
        {/* no eyebrow; headline sized to match the homepage section heads (type-h1). */}
        <header className="mb-[clamp(28px,3.5vw,48px)]">
          <motion.h2
            className="type-h1 text-charcoal"
            style={{ maxWidth: "20ch" }}
            variants={headlineContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            <motion.span className="block" variants={headlineLine}>
              AI workshops led by
            </motion.span>
            <motion.span className="block" variants={headlineLine}>
              <em style={{ color: "var(--terra)", fontStyle: "normal" }}>world-renowned</em> experts.
            </motion.span>
          </motion.h2>
          <p className="type-subhead text-charcoal-muted mt-5" style={{ maxWidth: "60ch" }}>
            From Fortune 100 firms to independent practitioners, our workshops draw the
            people who already treat building with AI as table stakes for the era ahead.
          </p>
        </header>

        {/* ── Client logo row — quiet, greyed proof above the board. NOT a
              marquee/slider; the logos fade in one-by-one on scroll, then sit
              static and muted (hover lifts a single logo to full ink). Flexes
              edge-to-edge so the first logo aligns left and the last hits the
              container's right edge. ── */}
        <ClientLogoRow reduce={reduce} />
      </div>

      {/* ── BREAK-OUT WIDTH: the board sits at the site's standard breakout
            container (1600px, centered) — wider than the 1280 header but NOT
            full-bleed. No frame border and no corner/registration marks — just
            the thin horizontal row rules (CELL_BORDER) of the grid itself. ── */}
      <div className="container-breakout mt-[clamp(32px,4vw,56px)]">
        <div className="relative bg-cream">
          <div className="relative z-10 px-[clamp(10px,1.8vw,28px)] py-[clamp(36px,4vw,60px)]">
            {/* ── Brutalist column-label header row — mono UPPERCASE labels on
                meaningful columns; non-labeled cells stay EMPTY. Top edge closes
                the table frame with the rows' bottom borders for a fully bordered
                grid with continuous lines. */}
          <div
            className="relative hidden lg:grid items-center"
            style={{ gridTemplateColumns: GRID_COLS, borderTop: CELL_BORDER }}
          >
            <span className="type-nav uppercase text-charcoal text-center py-3.5 px-2" style={{ marginBottom: 0 }} aria-hidden="true">Date</span>
            <span className="type-nav uppercase text-charcoal py-3.5 px-[clamp(16px,2vw,26px)]" style={{ marginBottom: 0 }} aria-hidden="true">Workshop</span>
            <span className="type-nav uppercase text-charcoal text-center py-3.5 px-2" style={{ marginBottom: 0 }} aria-hidden="true">Format</span>
            <span className="type-nav uppercase text-charcoal text-center py-3.5 px-2" style={{ marginBottom: 0 }} aria-hidden="true">Time</span>
            <span className="type-nav uppercase text-charcoal-muted text-center py-3.5 px-2" style={{ marginBottom: 0 }} aria-hidden="true">Reserve</span>

          </div>

          {/* ── The rows (mobile gets a top edge; desktop inherits from header).
                Plain wrapper — each ScheduleRow self-triggers its entrance on
                scroll, so async-mounting rows can't get orphaned at opacity:0. ── */}
          <div style={{ borderTop: CELL_BORDER }} className="lg:[border-top:none]" role="list">
            {rows.map((row) => (
              <ScheduleRow
                key={row.seriesSlug}
                row={row}
                isNextUp={row.seriesSlug === nextUpSlug}
                variant={rowVariant}
              />
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* ── Prominent VIEW ALL CTA — back at STANDARD container width ── */}
      <div className="container-content">
        <div className="mt-[clamp(28px,3.5vw,48px)] flex flex-col gap-[var(--gap-sm)] sm:flex-row sm:items-center sm:justify-between">
          <Link href="/workshops" className="btn btn-cta btn-lg btn-arrow">
            View all workshops
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
          <p className="type-label text-charcoal-muted !mb-0">
            400K+ trained &middot; capped cohorts &middot; recordings included
          </p>
        </div>
      </div>
    </section>
  );
}

export default WorkshopsSchedule;
